import { useRef, useState } from "react";
import { FileUp, Loader2, ScanText, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StatusChip, queueLabel } from "@/components/app/StatusChip";
import { openProtectedFile } from "@/lib/api";
import { cn } from "@/lib/utils";
import { JDWriter } from "@/pages/wizard/JDWriter";
import { PrimaryJobFields, BarRequired } from "@/pages/job/LegalFields";
import { CollapsibleSection } from "@/components/app/CollapsibleSection";

const MIN_CHARS = 100;

function UploadBox({ onUpload, uploadJob, fileName, fileUrl }) {
  const input = useRef(null);
  const busy = uploadJob && !["completed", "failed"].includes(uploadJob.status);
  return (
    <div className="flex flex-col items-start gap-3 rounded-[6px] border border-dashed border-line bg-subtle p-6" data-testid="jd-upload-box">
      <div className="flex items-center gap-3">
        <FileUp size={20} className="text-brand" />
        <div>
          <p className="text-sm font-medium text-ink">Upload a PDF or DOCX</p>
          <p className="text-xs text-ink-muted">Up to 10 MB. We'll extract the text so you can check it.</p>
        </div>
      </div>
      <input ref={input} type="file" accept=".pdf,.docx" className="hidden" data-testid="jd-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = ""; }} />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" disabled={busy} onClick={() => input.current?.click()} data-testid="jd-choose-file-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileUp size={14} />} Choose file
        </Button>
        {uploadJob && <StatusChip status={queueLabel(uploadJob.status)} testId="jd-upload-status" />}
        {fileName && fileUrl && (
          <button className="inline-flex items-center gap-1 text-sm text-brand hover:underline" data-testid="jd-view-original-btn" onClick={() => openProtectedFile(fileUrl)}>
            <FileText size={14} /> {fileName}
          </button>
        )}
      </div>
      {uploadJob?.status === "failed" && <p className="text-sm text-risk" data-testid="jd-upload-error">{uploadJob.error}</p>}
    </div>
  );
}

export function StepDescribe({ job, setJob, onUpload, uploadJob, onAnalyze, analyzing }) {
  const [mode, setMode] = useState("paste");
  const set = (k) => (e) => setJob((j) => ({ ...j, [k]: e.target.value }));
  const chars = (job.jd_text || "").trim().length;
  const primaryOk = !!(job.title || "").trim() && (!!(job.practice_area || "").trim() || job.pqe_min != null);
  const canGo = chars >= MIN_CHARS || primaryOk;

  return (
    <div className="space-y-6" data-testid="wizard-step-describe">
      <div className="panel p-6">
        <PrimaryJobFields form={job} setForm={setJob} prefix="job" />
      </div>

      <CollapsibleSection title="More details (optional)" subtitle="Description, department, employment type and bar enrolment. The AI uses the five fields above if you skip this." defaultOpen={chars > 0} testId="job-more-details">
        <div className="space-y-4 p-1">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="job-dept">Department</Label>
              <Input id="job-dept" value={job.department || ""} onChange={set("department")} placeholder="Optional" data-testid="job-department-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="job-type">Employment type</Label>
              <Input id="job-type" value={job.employment_type || ""} onChange={set("employment_type")} placeholder="e.g. Full-time" data-testid="job-type-input" />
            </div>
            <BarRequired form={job} set={(k, v) => setJob((j) => ({ ...j, [k]: v }))} prefix="job" />
          </div>
          <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2">
            <h2 className="text-lg font-semibold">Job description</h2>
            <div className="flex rounded-lg border border-line p-0.5" role="tablist">
              {[["paste", "Paste text"], ["upload", "Upload file"]].map(([k, l]) => (
                <button key={k} role="tab" data-testid={`jd-mode-${k}`} onClick={() => setMode(k)}
                  className={cn("rounded-md px-3 py-1 text-sm transition-colors duration-150", mode === k ? "bg-brand-tint text-brand font-medium" : "text-ink-secondary hover:text-ink")}>{l}</button>
              ))}
            </div>
          </div>
          {mode === "upload" && <UploadBox onUpload={onUpload} uploadJob={uploadJob} fileName={job.jd_file_name} fileUrl={job.jd_file_url} />}
          {mode === "paste" && <JDWriter job={job} setJob={setJob} />}
          <Textarea value={job.jd_text || ""} onChange={set("jd_text")} placeholder="Paste the full job description here (optional)…" className="min-h-[240px] text-sm leading-relaxed" data-testid="jd-text-input" />
          <p className="text-xs text-ink-muted" data-testid="jd-char-count">{chars.toLocaleString()} characters</p>
        </div>
      </CollapsibleSection>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {!canGo && <p className="text-sm text-caution" data-testid="wizard-analyze-hint">Add the job title and a practice area or PQE to continue.</p>}
        <Button onClick={onAnalyze} disabled={!canGo || analyzing} data-testid="wizard-analyze-btn">
          {analyzing ? <Loader2 size={16} className="animate-spin" /> : <ScanText size={16} />} Analyse with AI
        </Button>
      </div>
    </div>
  );
}
